import { Box } from '@superdispatch/ui-lab';
import { ShipmentSubhaulPreviewDTO } from '../data/VehiclesDTO';
import { CheckboxField, ColorDynamic, Inline, Stack } from '@superdispatch/ui';
import { Typography } from '@material-ui/core';
import { LoadWaypoints } from 'shared/ui/LoadWaypoints';
import { formatAddressLine } from 'shared/helpers/AddressHelpers';
import { formatDateRange, formatPickupDate } from '../data/VehicleUtils';
import { joinStrings } from 'shared/utils/StringUtils';
import { LoadDTO } from 'carrier/loads/data/LoadDTO';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { LoadsSidebarIcon } from 'shared/icons/LoadsSidebarIcon';
import { useSearchParams } from 'react-router-dom';

interface VehicleSubhaulLoadProps {
  load?: LoadDTO;
  shipment?: ShipmentSubhaulPreviewDTO;
  shipmentCount: number;
}

export function VehicleSubhaulLoad({
  load,
  shipment,
  shipmentCount,
}: VehicleSubhaulLoadProps) {
  const [filters, updateFilters] = useSearchParams();
  const isBundled = filters.get('bundle') === 'true';

  const [vehicle] = load?.guid ? load.vehicles : (shipment?.vehicles ?? []);

  const vehiclesCount = load?.guid
    ? load?.vehicles?.length
    : (shipment?.vehicles?.length ?? 0);

  return (
    <Box paddingLeft="large" paddingRight="large">
      <Box
        padding="small"
        borderColor="Silver400"
        borderWidth="small"
        borderRadius="small"
      >
        <Stack space="small">
          <Typography variant="h5">
            Load offer which selected carrier will get
          </Typography>
          <LoadWaypoints
            space="small"
            pickupAddress={
              <Inline verticalAlign="center">
                <Typography>
                  {load?.guid
                    ? formatAddressLine(
                        load.pickup.venue.zip,
                        load.pickup.venue.city,
                        load.pickup.venue.state,
                      )
                    : formatAddressLine(
                        shipment?.origin.zip,
                        shipment?.origin.city,
                        shipment?.origin.state,
                      ) || 'N/A'}
                </Typography>
                <Typography color="textSecondary">
                  {load?.guid
                    ? formatPickupDate(load.pickup.scheduled_date)
                    : formatPickupDate(shipment?.origin.latest_pickup_at)}
                </Typography>
              </Inline>
            }
            deliveryAddress={
              <Inline space="xxsmall" verticalAlign="center">
                <Typography>
                  {load?.guid
                    ? formatAddressLine(
                        load.delivery.venue.zip,
                        load.delivery.venue.city,
                        load.delivery.venue.state,
                      )
                    : formatAddressLine(
                        shipment?.destination.zip,
                        shipment?.destination.city,
                        shipment?.destination.state,
                      ) || 'N/A'}
                </Typography>
                <Typography color="textSecondary">
                  {load?.guid
                    ? formatDateRange(
                        load.delivery.actual_date,
                        load.delivery.scheduled_date,
                      )
                    : formatDateRange(
                        shipment?.destination.earliest_delivery_at,
                        shipment?.destination.latest_delivery_at,
                      )}
                </Typography>
              </Inline>
            }
          />
          <Stack space="xsmall">
            <Inline verticalAlign="center">
              <Typography>
                {joinStrings(' ', vehicle?.year, vehicle?.make, vehicle?.model)}
              </Typography>
              <Typography color="textSecondary">{vehicle?.type}</Typography>
            </Inline>
            {shipmentCount > 1 && (
              <CheckboxField
                checked={isBundled}
                label="Bundle vehicles if customer & route match"
                onChange={(e) => {
                  updateFilters((filter) => {
                    filter.set('bundle', `${e.target.checked}`);
                    return filter;
                  });
                }}
              />
            )}
          </Stack>
          {vehiclesCount > 0 && (
            <Inline verticalAlign="center" space="xxsmall">
              <LoadsSidebarIcon
                fontSize="small"
                htmlColor={ColorDynamic.Dark100}
              />
              <Typography color="textSecondary">
                {vehiclesCount}{' '}
                {formatPlural(vehiclesCount, 'vehicle', 'vehicles')}
              </Typography>
            </Inline>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
